import type { EventParticipantRole, EventType, ProgramEventInviteeRole, ProgramEventType } from 'models';
import { type ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'ui';

type EventRoleAction = {
  icon: ReactNode;
  key: string;
  label: string;
  role: EventParticipantRole;
};

type RoleAction = {
  key: string;
  label: string;
  role: ProgramEventInviteeRole;
};

const isTelehealthRole = (role: EventParticipantRole | ProgramEventInviteeRole): role is EventParticipantRole =>
  role !== 'presenter';

export const useGetEventRoleName = () => {
  const { t } = useTranslation();

  return useCallback(
    ({ isTelehealth, role }: { isTelehealth: boolean; role: EventParticipantRole | ProgramEventInviteeRole }) => {
      if (isTelehealth && isTelehealthRole(role)) {
        switch (role) {
          case 'attendee':
            return t('Patient / Guest');
          case 'moderator':
            return t('Provider');
          case 'presenter':
            return t('Presenter');
        }
      } else {
        switch (role) {
          case 'attendee':
            return t('Attendee');
          case 'moderator':
            return t('Admin / Host');
          case 'presenter':
            return t('Presenter');
        }
      }
    },
    [t],
  );
};

export const useGetEventRoleKey = () => {
  const { t } = useTranslation();

  return useCallback(
    ({ role }: { role: ProgramEventInviteeRole }) => {
      switch (role) {
        case 'attendee':
          return t('attendee');
        case 'moderator':
          return t('admin');
        case 'presenter':
          return t('presenter');
      }
    },
    [t],
  );
};

export const useGetRailsEventRoleActions = () =>
  useCallback(({ eventType }: { eventType: ProgramEventType }): RoleAction[] => {
    switch (eventType) {
      case 'conference':
      case 'webinar':
        return [
          { key: 'change-to-admin', label: 'Change to admin', role: 'moderator' },
          { key: 'change-to-presenter', label: 'Change to presenter', role: 'presenter' },
          { key: 'change-to-attendee', label: 'Change to attendee', role: 'attendee' },
        ];
    }
  }, []);

export const useGetEventRoleActions = () =>
  useCallback(({ eventType }: { eventType: EventType }): EventRoleAction[] => {
    switch (eventType) {
      case 'call':
      case 'clinical_review':
      case 'reminder':
        return [];
      case 'telehealth_visit':
      case 'video_conference':
      case 'webinar':
        return [
          {
            icon: <Icon icon="shield-shape-line" />,
            key: 'change-to-admin',
            label: 'Change to admin',
            role: 'moderator',
          },
          {
            icon: <Icon icon="presentation-1-line" />,
            key: 'change-to-presenter',
            label: 'Change to presenter',
            role: 'presenter',
          },
          { icon: <Icon icon="eye-line" />, key: 'change-to-attendee', label: 'Change to attendee', role: 'attendee' },
        ];
    }
  }, []);
